/**
 * Workplace AI Safety
 *
 * Shared prompt and runtime checks for workplace AI behavior.
 *
 * Primary responsibilities:
 * - Keep workplace psychology safeguards centralized
 * - Gate high-impact people actions before tool execution
 * - Provide recommendation rules that preserve user control
 *
 * Dependencies:
 * - None
 */

export const WORKPLACE_AI_SAFETY_PROMPT_SIGNATURE = 'WORKPLACE AI SAFETY PRINCIPLES'

export const WORKPLACE_AI_SAFETY_PROMPT = `
${WORKPLACE_AI_SAFETY_PROMPT_SIGNATURE}

Use these rules for workplace, team, customer, employee, contractor, or
manager-facing work:
- Preserve user control. Recommend, preview, and ask before acting when a
  choice affects people, commitments, permissions, money, schedules, or
  externally visible communication.
- Explain recommendations with the evidence used, the rationale, the affected
  people or records, and the expected tradeoff.
- Make assumptions, uncertainty, missing context, and confidence limits
  visible. Do not hide weak evidence behind polished language.
- Avoid manipulative urgency. Distinguish truly time-critical risk from noisy
  work, and do not pressure users with shame, fear, fake scarcity, or inflated
  priority.
- Separate assistant suggestions from accountable human decisions. Do not
  present an AI recommendation as a manager instruction.
- Never infer mental health, neurotype, personality, commitment, resilience,
  burnout, psychological safety, or productivity from passive behavior as an
  individual diagnosis or score.
- Avoid surveillance-style rankings, activity quotas, presence theater, or
  hidden employee scoring.

High-impact people decisions require explicit human review before any tool
call, write, message, calendar action, ticket, task, or integration action:
- Hiring, rejection, firing, termination, layoff, promotion, demotion,
  compensation, discipline, performance rating, PIP, reassignment, reduced
  hours, worker access, individual surveillance, or employment-impacting
  analytics.
- First provide a preview that includes evidence, rationale, uncertainty,
  alternatives, affected people, reversibility, and who is accountable.
- Ask the user to confirm that a human reviewed and approved the action. If
  confirmation is absent or ambiguous, do not execute; request review instead.
`.trim()

export const WORKPLACE_AI_TOOL_SAFETY_INSTRUCTIONS = `
## Workplace AI Safety

- Explain why a tool action is being recommended before executing it when the
  action affects people, commitments, permissions, money, schedules, or
  external communication.
- For high-impact people decisions, do not call write/send/update tools until
  the latest user message clearly confirms human review and approval.
- If evidence is incomplete, ask for the missing context or route to a human
  instead of guessing.
- Do not use urgent wording unless there is a concrete deadline, safety risk,
  customer commitment, legal/regulatory issue, or active incident.
`.trim()

export const WORKPLACE_RECOMMENDATION_SAFETY_PROMPT = `
Recommendation safety rules:
- Write suggestions as optional support, not commands or pressure.
- Include a brief evidence-based rationale and one uncertainty or caveat.
- Avoid urgency unless there is a concrete deadline, safety issue, customer
  commitment, legal/regulatory issue, or active incident.
- Do not recommend high-impact people decisions such as hiring, firing,
  promotion, pay, discipline, performance ratings, PIPs, individual
  surveillance, or psychological scoring.
- Do not infer mental health, neurotype, personality, commitment, burnout, or
  resilience from activity traces.
`.trim()

export type WorkplacePromptMessage = {
  role?: string
  content?: unknown
}

export type HighImpactPeopleActionCategory =
  | 'employment_decision'
  | 'people_analytics'
  | 'sensitive_inference'

export type HighImpactPeopleActionReview = {
  category: HighImpactPeopleActionCategory
  message: string
  signals: string[]
}

export type HighImpactPeopleActionBlockResult = {
  success: false
  code: 'APPROVAL_REQUIRED'
  error: string
  message: string
  human_review_required: true
  high_impact_people_action: true
  category: HighImpactPeopleActionCategory
  signals: string[]
  approvalRequest: {
    id: string
    functionName: string
    argumentsPreview: string
    reason: string
    requiresHumanReview: true
  }
}

const NON_HIRING_EMPLOYMENT_DECISION_PATTERN =
  /\b(reject candidate|candidate rejection|fire|firing|fired|terminate|termination|layoff|lay off|promote|promotion|demote|demotion|discipline|disciplinary|performance review|performance rating|pip|performance improvement plan|compensation|salary|pay raise|bonus|reassign|reassignment|reduced hours|reduce hours|schedule cut|probation)\b/i

const HIRING_DECISION_PATTERN =
  /\b(hire\s+(?:the\s+)?(?:candidate|applicant|employee|worker|contractor|person|them|him|her|[A-Z][A-Za-z'-]+)|hiring\s+(?:decision|recommendation|approval|score|ranking|shortlist|rubric|criteria|outcome|offer|rejection))\b/i

const EMPLOYMENT_DECISION_PATTERN = new RegExp(
  `${NON_HIRING_EMPLOYMENT_DECISION_PATTERN.source}|${HIRING_DECISION_PATTERN.source}`,
  'i'
)

const PEOPLE_ANALYTICS_PATTERN =
  /\b(productivity score|activity score|presence score|sentiment score|engagement score|burnout score|psychological safety score|resilience score|commitment score|rank employees|employee ranking|worker surveillance|employee surveillance|monitor employee|monitor worker|individual performance score)\b/i

const SENSITIVE_INFERENCE_PATTERN =
  /\b(mental health|depression|anxiety|adhd|autism|neurotype|neurodivergent|personality|protected class|disability|medical condition|psychological diagnosis|burnout diagnosis)\b/i

const MUTATING_TOOL_PATTERN =
  /\b(send|sent|create|created|update|updated|delete|deleted|assign|assigned|schedule|scheduled|cancel|cancelled|transfer|execute|executed|post|posted|message|email|call|invite|remove|removed|add|added|book|reschedule|approve|approved|reject|rejected|notify|notified)\b/i

const NOTE_TOOL_PATTERN = /\b(leave note|add note)\b/i
const INTERNAL_SPACE_CONTENT_TOOL_PATTERN =
  /\b(losiSpaces__space(?:Task|Note|Sheet|Folder|Sprint|Design|Context|Chat|Conversation|Whiteboard)Tool|space(?:Task|Note|Sheet|Folder|Sprint|Design|Context|Chat|Conversation|Whiteboard)Tool|VirtualFilesystem__losi__fs_(?:write|patch|copy|move)_file)\b/i
const HUMAN_HANDOFF_TOOL_PATTERN =
  /\b(transfer to human|transfer call|route to specialist agent|list available agents)\b/i

const HUMAN_REVIEW_CONFIRMATION_PATTERN =
  /\b(human|manager|hr|legal|manual|people team|accountable owner)\b.{0,80}\b(reviewed|approved|confirmed|authorized|authorised|signed off)\b|\b(reviewed|approved|confirmed|authorized|authorised|signed off)\b.{0,80}\b(human|manager|hr|legal|manual|people team|accountable owner)\b/i

function buildArgumentsPreview(args: unknown): string {
  try {
    return JSON.stringify(args).slice(0, 1200)
  } catch {
    return '[unserializable arguments]'
  }
}

function collectText(value: unknown, output: string[], depth = 0): void {
  if (output.join(' ').length > 12_000 || depth > 4 || value == null) {
    return
  }

  if (typeof value === 'string') {
    output.push(value)
    return
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    output.push(String(value))
    return
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      collectText(item, output, depth + 1)
    }
    return
  }

  if (typeof value === 'object') {
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      output.push(key)
      collectText(item, output, depth + 1)
    }
  }
}

function textFromUnknown(value: unknown): string {
  const output: string[] = []
  collectText(value, output)
  return output.join(' ').slice(0, 12_000)
}

function getRecentUserText(messages?: ReadonlyArray<WorkplacePromptMessage>): string {
  if (!Array.isArray(messages)) {
    return ''
  }

  return [...messages]
    .reverse()
    .filter((message) => message.role === 'user')
    .slice(0, 2)
    .map((message) => textFromUnknown(message.content))
    .join('\n')
}

function findHighImpactSignals(text: string) {
  const signals: string[] = []
  let category: HighImpactPeopleActionCategory | null = null

  if (EMPLOYMENT_DECISION_PATTERN.test(text)) {
    category = 'employment_decision'
    signals.push('employment_decision')
  }

  if (PEOPLE_ANALYTICS_PATTERN.test(text)) {
    category = category ?? 'people_analytics'
    signals.push('people_analytics')
  }

  if (SENSITIVE_INFERENCE_PATTERN.test(text)) {
    category = category ?? 'sensitive_inference'
    signals.push('sensitive_inference')
  }

  return category ? { category, signals } : null
}

function getActionDescriptor(toolName: string, args: unknown): string {
  if (!args || typeof args !== 'object' || Array.isArray(args)) {
    return toolName.replace(/[_-]+/g, ' ')
  }

  const record = args as Record<string, unknown>
  const actionFields = ['action', 'actionId', 'action_id', 'extensionActionId', 'type', 'mode']
  const actionValues = actionFields
    .map((field) => record[field])
    .filter((value): value is string => typeof value === 'string')

  return `${toolName} ${actionValues.join(' ')}`.replace(/[_-]+/g, ' ')
}

function isMutatingToolAction(toolName: string, args: unknown): boolean {
  const actionDescriptor = getActionDescriptor(toolName, args)

  if (HUMAN_HANDOFF_TOOL_PATTERN.test(actionDescriptor)) {
    return false
  }

  return NOTE_TOOL_PATTERN.test(actionDescriptor) || MUTATING_TOOL_PATTERN.test(actionDescriptor)
}

function isInternalSpaceContentAction(toolName: string, args: unknown): boolean {
  if (!INTERNAL_SPACE_CONTENT_TOOL_PATTERN.test(toolName)) {
    return false
  }

  const actionDescriptor = getActionDescriptor(toolName, args)
  return /\b(create|created|update|updated|write|patch|copy|move|comment|attach|link)\b/i.test(
    actionDescriptor
  )
}

export function appendWorkplaceAiSafetyPromptToText(systemPrompt: string): string {
  if (systemPrompt.includes(WORKPLACE_AI_SAFETY_PROMPT_SIGNATURE)) {
    return systemPrompt
  }

  return systemPrompt.trim()
    ? `${systemPrompt}\n\n${WORKPLACE_AI_SAFETY_PROMPT}`
    : WORKPLACE_AI_SAFETY_PROMPT
}

export function appendWorkplaceAiSafetyPrompt(messages: WorkplacePromptMessage[]): void {
  const firstMessage = messages[0]
  const block = `\n\n${WORKPLACE_AI_SAFETY_PROMPT}`

  if (firstMessage?.role === 'system') {
    if (textFromUnknown(firstMessage.content).includes(WORKPLACE_AI_SAFETY_PROMPT_SIGNATURE)) {
      return
    }

    firstMessage.content =
      typeof firstMessage.content === 'string'
        ? `${firstMessage.content}${block}`
        : Array.isArray(firstMessage.content)
          ? [
              ...firstMessage.content,
              {
                type: 'text',
                text: block
              }
            ]
          : appendWorkplaceAiSafetyPromptToText(textFromUnknown(firstMessage.content))
    return
  }

  messages.unshift({
    role: 'system',
    content: WORKPLACE_AI_SAFETY_PROMPT
  })
}

export function containsHighImpactPeopleSignal(value: unknown): boolean {
  return Boolean(findHighImpactSignals(textFromUnknown(value)))
}

export function hasRecentHighImpactPeopleConfirmation(
  messages?: ReadonlyArray<WorkplacePromptMessage>
): boolean {
  return HUMAN_REVIEW_CONFIRMATION_PATTERN.test(getRecentUserText(messages))
}

export function getHighImpactPeopleActionReview({
  toolName,
  args,
  messages
}: {
  toolName?: string | null
  args?: unknown
  messages?: ReadonlyArray<WorkplacePromptMessage>
}): HighImpactPeopleActionReview | null {
  const normalizedToolName = typeof toolName === 'string' ? toolName : ''
  const actionText = textFromUnknown(args)
  const signalMatch = findHighImpactSignals(`${normalizedToolName} ${actionText}`)

  if (
    !signalMatch ||
    isInternalSpaceContentAction(normalizedToolName, args) ||
    !isMutatingToolAction(normalizedToolName, args) ||
    hasRecentHighImpactPeopleConfirmation(messages)
  ) {
    return null
  }

  return {
    category: signalMatch.category,
    signals: signalMatch.signals,
    message:
      'High-impact people actions need explicit human review before execution. Preview the evidence, rationale, uncertainty, alternatives, affected people, reversibility, and accountable human, then ask the user to confirm human review and approval.'
  }
}

export function buildHighImpactPeopleActionBlockResult(
  review: HighImpactPeopleActionReview,
  toolName = 'unknown_tool',
  args?: unknown
): HighImpactPeopleActionBlockResult {
  return {
    success: false,
    code: 'APPROVAL_REQUIRED',
    error: review.message,
    message: review.message,
    human_review_required: true,
    high_impact_people_action: true,
    category: review.category,
    signals: review.signals,
    approvalRequest: {
      id: `human-review-required-${toolName}`,
      functionName: toolName,
      argumentsPreview: buildArgumentsPreview(args),
      reason: review.message,
      requiresHumanReview: true
    }
  }
}
